/**
 * Every GitHub repository available to link, with the warehouse each one is linked to. The agent reads
 * only the linked ones. The link queries run only while the sheet is open, because the body mounts with
 * the sheet content.
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, X } from 'lucide-react';
import { createContext, useContext, useMemo, useState } from 'react';

import { SearchInput } from '@/_shared/components/SearchInput';
import { Table, type TableColumn } from '@/_shared/components/Table';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/_shared/components/ui/sheet';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { useOrpc } from '@/_shared/hooks/useOrpc';

import { accountKeyDisplay } from '../lib/linkedRepoList';
import { projectTypeLabel } from '../lib/projectType';
import { repoCountLabel } from './repo-utils';
import { ScmProviderIcon } from './ScmProviderIcon';
import { nameWarehouseAccounts } from './warehouseAccount/useWarehouseAccounts';
import { warehouseTypeMeta } from './warehouseAccount/warehouseTypes';

export type GithubRepo = Awaited<ReturnType<RouterClient<AppRouter>['settings']['scm']['listRepos']>>['repos'][number];

interface RepoLink {
  id: string;
  warehouseName: string;
  warehouseType: string;
  projectType: string;
  /** Where the project sits in the repo. `/` (or empty) is the repo root. */
  projectPath: string;
  /** No account has the link's key. */
  disconnected: boolean;
}

interface RepoRow {
  id: string;
  fullName: string;
  /** The repo's GitHub page, or null when the API sent something other than an https URL. */
  htmlUrl: string | null;
}

type LinksState = 'loading' | 'failed' | 'ready';

/**
 * The links reach the "Linked to" cells through context, not through the table rows. A link query that
 * completes then leaves the table data unchanged, so the table keeps its scroll position. (A new
 * `columns` array is not enough: the table's row model depends only on `data`, so memoized rows would
 * keep the old cells.)
 */
const RepoLinksContext = createContext<{ linksByRepo: ReadonlyMap<string, RepoLink[]>; state: LinksState }>({
  linksByRepo: new Map(),
  state: 'loading',
});

function RepoName({ fullName, htmlUrl }: { fullName: string; htmlUrl: string | null }): React.ReactElement {
  const slash = fullName.indexOf('/');
  const name = (
    <span className="truncate">
      {slash > 0 && <span className="text-muted-foreground">{fullName.slice(0, slash + 1)}</span>}
      <span className="font-medium text-foreground">{slash > 0 ? fullName.slice(slash + 1) : fullName}</span>
    </span>
  );
  if (!htmlUrl) {
    return (
      <span className="flex min-w-0" title={fullName}>
        {name}
      </span>
    );
  }
  return (
    <a
      href={htmlUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={fullName}
      aria-label={`${fullName} (opens in a new tab)`}
      className="group inline-flex max-w-full min-w-0 items-center gap-1 rounded-sm focus-ring hover:underline"
    >
      {name}
      {/* Hidden until hover or keyboard focus; it keeps its space, so the name does not shift. */}
      <ExternalLink
        className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        aria-hidden
      />
    </a>
  );
}

function LinkedTo({ fullName }: { fullName: string }): React.ReactElement {
  const { linksByRepo, state } = useContext(RepoLinksContext);
  if (state === 'loading') return <Skeleton className="h-4 w-28" />;
  // Without the link data, "Not linked" would be a guess. The callout above the table explains why.
  if (state === 'failed') return <span className="text-muted-foreground">Unknown</span>;

  const links = linksByRepo.get(fullName.toLowerCase()) ?? [];
  if (links.length === 0) return <span className="text-muted-foreground">Not linked</span>;
  return (
    <div className="flex flex-col gap-1">
      {links.map((link) => {
        const { Icon } = warehouseTypeMeta(link.warehouseType);
        const hasPath = link.projectPath !== '' && link.projectPath !== '/';
        return (
          <span key={link.id} className="flex min-w-0 flex-col">
            <span className="flex min-w-0 items-center gap-1.5">
              <Icon className="size-4 shrink-0" />
              <span className="truncate">{link.warehouseName}</span>
              <span className="shrink-0 text-muted-foreground">· {projectTypeLabel(link.projectType)}</span>
              {link.disconnected && (
                <Badge variant="warning" className="shrink-0">
                  Disconnected
                </Badge>
              )}
            </span>
            {/* One repo can link to the same warehouse at several paths; the path tells those links apart. */}
            {hasPath && (
              <span className="truncate pl-5.5 text-xs text-muted-foreground" title={link.projectPath}>
                Path: {link.projectPath}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

const COLUMNS: TableColumn<RepoRow>[] = [
  {
    id: 'fullName',
    header: 'Repository',
    cell: (row) => <RepoName fullName={row.fullName} htmlUrl={row.htmlUrl} />,
  },
  {
    id: 'links',
    header: 'Linked to',
    width: '15rem',
    cell: (row) => <LinkedTo fullName={row.fullName} />,
  },
];

function GithubReposSheetBody({ repos }: { repos: GithubRepo[] }): React.ReactElement {
  const orpc = useOrpc();
  const [search, setSearch] = useState('');

  const projectsQuery = useQuery(orpc.warehouseProjects.list.queryOptions({ input: {} }));
  const accountsQuery = useQuery(orpc.settings.warehouses.listAccounts.queryOptions());
  // A link needs both queries: the projects hold the links, and the accounts show which warehouses still
  // exist. `isPending`, not `isLoading`, so that a query paused offline shows as loading.
  const linksState: LinksState =
    projectsQuery.isError || accountsQuery.isError
      ? 'failed'
      : projectsQuery.isPending || accountsQuery.isPending
        ? 'loading'
        : 'ready';

  const linksByRepo = useMemo(() => {
    // Projects link to a warehouse BUCKET (`accountKey`), which can hold several stored connections.
    const accountsByKey = new Map<string, { name: string; type: string }[]>();
    for (const account of nameWarehouseAccounts(accountsQuery.data?.accounts ?? [])) {
      const bucket = accountsByKey.get(account.accountKey) ?? [];
      bucket.push({ name: account.name, type: account.type });
      accountsByKey.set(account.accountKey, bucket);
    }

    const byRepo = new Map<string, RepoLink[]>();
    for (const project of projectsQuery.data?.projects ?? []) {
      const accounts = accountsByKey.get(project.accountKey) ?? [];
      // A link outlives its account (ACT2-1133): with no account, it is listed as Disconnected, named
      // by the key's database, so the admin can see it and fix it in Settings → Context.
      const display = accountKeyDisplay(project.accountKey);
      const key = project.repoFullName.toLowerCase();
      const links = byRepo.get(key) ?? [];
      links.push({
        id: project.id,
        warehouseName: accounts.length > 0 ? accounts.map((account) => account.name).join(', ') : display.database,
        warehouseType: accounts[0]?.type ?? display.type,
        projectType: project.projectType,
        projectPath: project.projectPath,
        disconnected: project.accountStatus === 'disconnected',
      });
      byRepo.set(key, links);
    }
    return byRepo;
  }, [projectsQuery.data?.projects, accountsQuery.data?.accounts]);

  const linksValue = useMemo(() => ({ linksByRepo, state: linksState }), [linksByRepo, linksState]);

  // Depends only on the repos, so the table data changes only when the repos or the search change.
  const rows = useMemo<RepoRow[]>(
    () =>
      repos
        .map((repo) => ({
          id: `${repo.installationId}:${repo.fullName}`,
          fullName: repo.fullName,
          htmlUrl: /^https:\/\//i.test(repo.htmlUrl) ? repo.htmlUrl : null,
        }))
        .sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [repos],
  );

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? rows.filter((row) => row.fullName.toLowerCase().includes(query)) : rows;
  }, [rows, search]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
      <SearchInput value={search} onChange={setSearch} placeholder="Search repositories..." />
      {linksState === 'failed' && (
        <Callout variant="error">Failed to load the warehouse links for these repositories.</Callout>
      )}
      <div className="min-h-0 flex-1">
        <RepoLinksContext.Provider value={linksValue}>
          <Table
            data={visible}
            columns={COLUMNS}
            getRowId={(row) => row.id}
            fixedLayout
            fillHeight
            emptyState="No matching repositories."
          />
        </RepoLinksContext.Provider>
      </div>
    </div>
  );
}

/**
 * The "View repositories" button and the sheet it opens. The button is the sheet's trigger, not a
 * controlled `open`: when the sheet closes, Radix returns focus to its trigger. With no trigger, focus
 * goes to <body>, and the next Tab starts at the top of the page.
 */
export function GithubReposSheet({ repos }: { repos: GithubRepo[] }): React.ReactElement {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="secondary" label="View repositories" />
      </SheetTrigger>
      <SheetContent side="right" showCloseButton={false} className="w-full gap-0 sm:max-w-drawer">
        <SheetHeader className="border-b border-border p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-3">
              <ScmProviderIcon size={20} />
              <span className="flex min-w-0 flex-col">
                <SheetTitle className="truncate text-sm font-medium text-foreground">GitHub repositories</SheetTitle>
                <SheetDescription className="text-xs text-muted-foreground">
                  {repoCountLabel(repos.length)} available to link
                </SheetDescription>
              </span>
            </span>
            <SheetClose asChild>
              <Button type="button" variant="tertiary" iconOnly aria-label="Close" Icon={<X />} />
            </SheetClose>
          </div>
        </SheetHeader>
        <GithubReposSheetBody repos={repos} />
      </SheetContent>
    </Sheet>
  );
}
